// src/components/appointments/RescheduleAppointmentModal.tsx
import React, { useState, useEffect, useMemo } from "react";
import { DateInput } from "../ui/DateInput";
import { toast } from "react-toastify";
import { AppointmentAPI } from "../../services/appointment.service";
import type { AppointmentSlot, RescheduleReason } from "../../services/appointment.service";
import { ApiError } from "../../services/api.errors";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";

const REASON_OPTIONS: { value: RescheduleReason; label: string }[] = [
  { value: "unwell", label: "Patient unwell" },
  { value: "work_conflict", label: "Work conflict" },
  { value: "transport", label: "Transport issue" },
  { value: "other", label: "Other" },
];

interface Props {
  isOpen: boolean;
  appointmentId: string;
  doctorId: string;
  currentSlotId?: string;
  currentSlotStart?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const fmtDateLabel = (dateStr: string) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", {
    weekday: "short", day: "numeric", month: "short",
  });

export const RescheduleAppointmentModal: React.FC<Props> = ({
  isOpen,
  appointmentId,
  doctorId,
  currentSlotId,
  onClose,
  onSuccess,
}) => {
  const [slots, setSlots] = useState<AppointmentSlot[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [reason, setReason] = useState<RescheduleReason | "">("");
  const [reasonNote, setReasonNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  // Set only on a 409 patient_slot_conflict — replaces the toast with an
  // inline banner offering an explicit "reschedule anyway?" override, rather
  // than silently blocking the double-booking.
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  const todayStr = toDateStr(new Date());
  const maxDateStr = toDateStr(new Date(Date.now() + 14 * 86_400_000));

  useEffect(() => {
    if (!isOpen || !doctorId) { setSlots([]); setSelectedSlotId(""); setDateFilter(""); return; }
    setSlotsLoading(true);
    AppointmentAPI.listSlots({
      doctor_id: doctorId,
      start_date: new Date(todayStr).toISOString(),
      end_date: new Date(maxDateStr + "T23:59:59").toISOString(),
    })
      .then((all) => {
        // keep future slots + current slot so it shows as "Current"
        const now = new Date();
        setSlots(all.filter(s => new Date(s.start_time) > now || s.slot_id === currentSlotId));
      })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, doctorId]); // currentSlotId/maxDateStr/todayStr are filter values, not fetch triggers

  // Group slots by date string
  const grouped = useMemo(() => {
    const map: Record<string, AppointmentSlot[]> = {};
    for (const s of slots) {
      const d = toDateStr(new Date(s.start_time));
      if (!map[d]) map[d] = [];
      map[d].push(s);
    }
    return map;
  }, [slots]);

  const filteredDates = useMemo(() => {
    const dates = Object.keys(grouped).sort();
    return dateFilter ? dates.filter(d => d === dateFilter) : dates;
  }, [grouped, dateFilter]);

  const availableCount = useMemo(
    () => slots.filter(s => !s.is_blocked && s.booked_count < s.capacity && s.slot_id !== currentSlotId).length,
    [slots, currentSlotId]
  );

  const handleReschedule = async (overridePatientConflict = false) => {
    if (!selectedSlotId) { toast.error("Select a new slot"); return; }
    if (!reason) { toast.error("Select a reason"); return; }
    if (reason === "other" && !reasonNote.trim()) { toast.error("Add a note for \"Other\""); return; }
    setConflictMessage(null);
    setLoading(true);
    try {
      await AppointmentAPI.reschedule(
        appointmentId, selectedSlotId, reason, reasonNote || undefined, overridePatientConflict,
      );
      toast.success("Appointment rescheduled");
      onSuccess();
      onClose();
    } catch (e: any) {
      // A 403 is the server answering, so it must not read as "you are offline".
      if (isPermissionDenied(e)) {
        toast.error(actionErrorMessage(e, "Failed to reschedule"));
      } else if (isBackendUnreachable(e)) {
        toast.error("You are offline — rescheduling requires a connection");
      } else if (e instanceof ApiError && e.isConflict && e.details.code === "patient_slot_conflict") {
        setConflictMessage(e.message);
      } else {
        toast.error(e.message || "Failed to reschedule");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Reschedule Appointment</h2>
            {!slotsLoading && (
              <p className="text-xs text-slate-400 mt-0.5">
                {availableCount} available slot{availableCount !== 1 ? "s" : ""} in the next 14 days
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold leading-none">×</button>
        </div>

        <div className="px-6 pt-4 pb-2 flex-shrink-0 space-y-3">
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            The current appointment will be cancelled and a new one created on the selected slot.
          </p>

          {/* Date filter */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 font-medium flex-shrink-0">Filter by date</label>
            <DateInput
              value={dateFilter}
              min={todayStr}
              max={maxDateStr}
              onChange={(e) => { setDateFilter(e.target.value); setSelectedSlotId(""); }}
              className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter("")}
                className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1.5 rounded-lg hover:bg-slate-100"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Slot list — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-2 min-h-0">
          {slotsLoading ? (
            <div className="space-y-3 py-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : filteredDates.length === 0 ? (
            <div className="py-8 text-center text-slate-400 space-y-1">
              <p className="text-sm font-medium text-slate-500">
                {dateFilter ? "No slots on this date." : "No available slots in the next 14 days."}
              </p>
              <p className="text-xs">Ask an admin to add slots for {dateFilter ? "this date" : "the doctor's schedule"}.</p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {filteredDates.map((dateStr) => {
                const daySlots = grouped[dateStr];
                const isToday = dateStr === todayStr;
                return (
                  <div key={dateStr}>
                    {/* Date header */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-slate-500">{fmtDateLabel(dateStr)}</span>
                      {isToday && (
                        <span className="text-[10px] font-medium text-cyan-600 bg-cyan-50 border border-cyan-200 rounded-full px-1.5 py-0.5">Today</span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {daySlots.map((slot) => {
                        const isCurrent = slot.slot_id === currentSlotId;
                        const full = slot.booked_count >= slot.capacity;
                        const selected = selectedSlotId === slot.slot_id;
                        const disabled = slot.is_blocked || full || isCurrent;
                        return (
                          <button
                            key={slot.slot_id}
                            onClick={() => !disabled && setSelectedSlotId(slot.slot_id)}
                            disabled={disabled}
                            className={`rounded-xl border px-3 py-2.5 text-left transition ${
                              selected
                                ? "border-cyan-500 bg-cyan-50 text-cyan-700 shadow-sm"
                                : isCurrent
                                ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                                : disabled
                                ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                                : "border-slate-200 hover:border-cyan-300 hover:bg-cyan-50/50 text-slate-700"
                            }`}
                          >
                            <div className="text-sm font-semibold">
                              {fmtTime(slot.start_time)}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                              {fmtTime(slot.start_time)} – {fmtTime(slot.end_time)}
                            </div>
                            <div className={`text-[11px] mt-1 font-medium ${
                              isCurrent ? "text-slate-400"
                              : slot.is_blocked ? "text-slate-400"
                              : full ? "text-red-500"
                              : "text-green-600"
                            }`}>
                              {isCurrent ? "Current" : slot.is_blocked ? "Blocked" : full ? "Full" : `${slot.capacity - slot.booked_count} spot${slot.capacity - slot.booked_count !== 1 ? "s" : ""} left`}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex-shrink-0 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Reason for rescheduling</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value as RescheduleReason)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400"
            >
              <option value="" disabled>Select a reason…</option>
              {REASON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {(reason === "other" || reasonNote) && (
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Note {reason === "other" ? "(required)" : "(optional)"}
              </label>
              <textarea
                value={reasonNote}
                onChange={(e) => setReasonNote(e.target.value)}
                rows={2}
                placeholder="Add a note…"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400 placeholder:text-slate-300"
              />
            </div>
          )}

          {conflictMessage && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-2">
              <p>{conflictMessage}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConflictMessage(null)}
                  className="flex-1 border border-amber-300 text-amber-700 py-1.5 rounded-lg hover:bg-amber-100 transition"
                >
                  Choose a different slot
                </button>
                <button
                  onClick={() => handleReschedule(true)}
                  disabled={loading}
                  className="flex-1 bg-amber-500 text-white py-1.5 rounded-lg font-medium hover:bg-amber-600 disabled:opacity-40 transition"
                >
                  Reschedule anyway
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => handleReschedule(false)}
              disabled={!selectedSlotId || !reason || loading}
              className="flex-1 bg-cyan-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-cyan-600 disabled:opacity-40 transition"
            >
              {loading ? "Rescheduling…" : "Reschedule"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
